
import React from 'react';
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, BookOpen, Target, Award, Activity } from 'lucide-react';
import type { Statistics } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

interface StatisticsCardsProps {
  statistics: Statistics | null;
}

export const StatisticsCards: React.FC<StatisticsCardsProps> = ({ statistics }) => {
  const { isMobile } = useIsMobile();
  
  if (!statistics) return null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
      <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-shadow duration-300`}>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className={`${isMobile ? "p-2" : "p-3"} bg-purple-100 rounded-lg`}>
            <BookOpen className={`${isMobile ? "h-4 w-4" : "h-6 w-6"} text-purple-600`} />
          </div>
          <div>
            <h3 className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-gray-500`}>Total Quizzes</h3>
            <p className={`${isMobile ? "text-lg" : "text-2xl"} font-bold mt-1 text-purple-600`}>{statistics.totalQuizzes}</p>
          </div>
        </div>
      </Card>

      <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-blue-50 to-white hover:shadow-lg transition-shadow duration-300`}>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className={`${isMobile ? "p-2" : "p-3"} bg-blue-100 rounded-lg`}>
            <Target className={`${isMobile ? "h-4 w-4" : "h-6 w-6"} text-blue-600`} />
          </div>
          <div>
            <h3 className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-gray-500`}>Average Score</h3>
            <p className={`${isMobile ? "text-lg" : "text-2xl"} font-bold mt-1 text-blue-600`}>{statistics.averageScore.toFixed(1)}%</p>
          </div>
        </div>
      </Card>

      <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-green-50 to-white hover:shadow-lg transition-shadow duration-300`}>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className={`${isMobile ? "p-2" : "p-3"} bg-green-100 rounded-lg`}>
            <Award className={`${isMobile ? "h-4 w-4" : "h-6 w-6"} text-green-600`} />
          </div>
          <div>
            <h3 className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-gray-500`}>Best Score</h3>
            <p className={`${isMobile ? "text-lg" : "text-2xl"} font-bold mt-1 text-green-600`}>{statistics.bestScore.toFixed(1)}%</p>
          </div>
        </div>
      </Card>

      <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-teal-50 to-white hover:shadow-lg transition-shadow duration-300`}>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className={`${isMobile ? "p-2" : "p-3"} bg-teal-100 rounded-lg`}>
            <Activity className={`${isMobile ? "h-4 w-4" : "h-6 w-6"} text-teal-600`} />
          </div>
          <div>
            <h3 className={`${isMobile ? "text-xs" : "text-sm"} font-medium text-gray-500`}>Recent Trend</h3>
            <div className="flex items-center gap-1 sm:gap-2 mt-1">
              {statistics.improving ? (
                <>
                  <TrendingUp className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-green-500`} />
                  <span className={`${isMobile ? "text-sm" : "text-xl"} font-bold text-green-600`}>Improving</span>
                </>
              ) : (
                <>
                  <TrendingDown className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-red-500`} />
                  <span className={`${isMobile ? "text-sm" : "text-xl"} font-bold text-red-600`}>Need Focus</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
